import { z } from 'zod';
import { docSentence } from './reports/docStrings';
import { currentLanguage } from './i18n';

/* =============================================================================
 * THE POLICY DOCUMENT
 * -----------------------------------------------------------------------------
 * Everything the business does numerically lives here, and the admin owns all
 * of it. There is not one `.default()` in this file — deliberately. A default
 * is an assumption, and an assumption about somebody's overtime rate is exactly
 * the kind of thing that quietly underpays a man for six months before anyone
 * notices.
 *
 * If a value is not set, the policy will not validate; if it does not validate,
 * it cannot be activated; and if no policy is active, payroll refuses to run.
 * ========================================================================== */

const positive = z.number().positive();
const nonNegative = z.number().min(0);
const hourOfDay = z.number().int().min(0).max(23);
const multiplier = z.number().min(0).max(10);
const percent = z.number().min(0).max(100);

/* --- Working day ---------------------------------------------------------- */
/**
 * A break at a fixed time of day, on named weekdays.
 *
 * Different in kind from workday.breakMinutes, which is a length subtracted
 * from every day wherever it happened to fall. This one has a clock time, and
 * that is what makes it possible to say a man came back late from it — a
 * duration on its own cannot be late.
 *
 * `days` uses the same numbering as weeklyRestDays: 0 is Sunday. A break on no
 * days is one that does not apply, which is how a company switches one off for
 * a season without losing what it was.
 */

/* -----------------------------------------------------------------------------
 * Names the company chose, in both languages
 * -----------------------------------------------------------------------------
 * A leave type called "Annual leave", an allowance called "Site allowance", a
 * break called "Lunch" — the interface around them is translated and they are
 * not, because the company typed them and we cannot know what it would write in
 * Arabic. "Site allowance" is not a phrase with one right answer; a firm that
 * calls it بدل موقع and one that calls it علاوة الموقع are both correct about
 * their own payslips.
 *
 * So each carries an optional sibling. Optional is the important part: null
 * means nobody has said, and the reader falls back to the English rather than
 * showing a blank where a name belongs. Storing the English in the Arabic field
 * would read as translated and be indistinguishable, later, from a translation
 * that happens to be in Latin script.
 *
 * `code` never gets one. It is matched against, not read.
 * -------------------------------------------------------------------------- */
export const breakSlotSchema = z.object({
  label: z.string().min(1),
  /** The same label in Arabic. Optional; falls back to `label`. */
  labelAr: z.string().optional(),
  /** 0 Sunday … 6 Saturday. */
  days: z.array(z.number().int().min(0).max(6)),
  /** Minutes from midnight, local. 12:00 is 720. */
  startMinute: z.number().int().min(0).max(1439),
  endMinute: z.number().int().min(1).max(1440),
  /**
   * Whether the break itself is paid.
   *
   * Unpaid is the usual arrangement, and is also what the gap between two
   * shifts produces on its own — a man who scans out and back in is not paid
   * for the time between. Paid adds the scheduled length back, so a crew that
   * takes its break without scanning out is not penalised for the honesty.
   */
  paid: z.boolean(),
});

export const workdaySchema = z.object({
  standardHoursPerDay: positive.max(24),
  standardDaysPerWeek: positive.max(7),
  /**
   * Which hour begins a working day. Set to 4 and a shift running 20:00–05:00
   * is recorded as one night's work, not as two half-days either side of
   * midnight.
   */
  dayBoundaryHour: hourOfDay,
  /** 0 = Sunday … 6 = Saturday. */
  weeklyRestDays: z.array(z.number().int().min(0).max(6)),
  breakMinutes: z.number().int().min(0).max(480),
  breakPaid: z.boolean(),
  /**
   * Breaks at fixed times, by weekday.
   *
   * Empty for a company that only uses the flat breakMinutes above, which is
   * every company until somebody fills this in.
   */
  breaks: z.array(breakSlotSchema).default([]),
  /**
   * What to do when a man comes back later than the break he was given.
   *
   * A two-hour break and a three-hour absence is an hour late. The gap itself
   * is already unpaid — he was not there — so this decides whether that hour is
   * additionally taken off the hours he did work.
   *
   *   ignore  — the missing hour is its own consequence. He is paid for what he
   *             worked and nothing further happens.
   *   deduct  — the minutes he was late come off his worked time as well. A
   *             deliberate penalty, and the reason it is a setting: it is a
   *             disciplinary decision rather than an arithmetic one.
   */
  lateFromBreak: z.enum(['ignore', 'deduct']).default('deduct'),
  /**
   * When duty begins, in minutes from midnight. 07:00 is 420.
   *
   * What "on time" is measured against, on the monthly grid and the payslip.
   * It decides a colour and nothing else: pay is built from the hours worked,
   * and this does not move a single minute of them.
   *
   * Optional, and absent means the check-in is shown without a verdict. A
   * company that has never said when its day starts has not said anybody is
   * late, and a screen painting half the crew red on a guess would be making
   * that accusation for it.
   */
  dutyStartMinute: z.number().int().min(0).max(1439).nullish(),
  /**
   * Minutes either side that still count.
   *
   * Arriving within this many minutes of the start is on time, and leaving
   * within this many minutes of a full day is a full day. One figure both
   * ways, because a scan two minutes off is the same imprecision at either
   * end of the shift.
   */
  lateGraceMinutes: z.number().int().min(0).max(120).nullish(),
  ramadanEnabled: z.boolean(),
  ramadanHoursPerDay: positive.max(24),
  /*
   * Optional, not merely nullable, and the difference is the whole reason these
   * were flagged as missing for ever.
   *
   * `.nullable()` accepts an explicit null and rejects a value that was never
   * set — so an admin who had simply not entered Ramadan dates could never
   * clear the warning, because nothing in the wizard writes `null`.
   *
   * They also move every year and belong to a period rather than to the
   * company, so "not set" is a legitimate, permanent state meaning no Ramadan
   * window is configured. That is safe: absent dates mean the reduced hours
   * never apply, which is the same as ramadanEnabled being off.
   */
  ramadanFrom: z.string().nullish(),
  ramadanTo: z.string().nullish(),
});

/* --- Overtime -------------------------------------------------------------- */
export const overtimeSchema = z.object({
  enabled: z.boolean(),
  /** Which salary figure the hourly rate is derived from. */
  baseFor: z.enum(['basic', 'gross']),
  normalMultiplier: multiplier,
  nightMultiplier: multiplier,
  nightStartHour: hourOfDay,
  nightEndHour: hourOfDay,
  restDayMultiplier: multiplier,
  holidayMultiplier: multiplier,
  /**
   * Exceeding the cap raises a compliance flag on the dashboard. It never
   * silently discards the hours — a man who worked them gets paid for them,
   * and the admin decides what to do about the breach.
   */
  dailyCapHours: positive.max(24),
  periodCapHours: positive,
  periodCapDays: z.number().int().positive(),
  roundingMinutes: z.number().int().min(1).max(60),
  roundingMode: z.enum(['up', 'down', 'nearest']),
});

/* --- Salary ---------------------------------------------------------------- */
export const salarySchema = z.object({
  /**
   * How a monthly salary becomes a daily rate.
   *   fixed        — always divide by `fixedDivisor` (commonly 30)
   *   calendarDays — divide by the actual length of the month
   *   workingDays  — divide by the month's working days, rest days excluded
   */
  dayRateDivisor: z.enum(['fixed', 'calendarDays', 'workingDays']),
  fixedDivisor: positive.max(31),
  /**
   * dayRate       — hourly = daily rate ÷ standard hours per day
   * monthlyHours  — hourly = monthly salary ÷ (standard hours × divisor)
   */
  hourRateBasis: z.enum(['dayRate', 'monthlyHours']),
  /**
   * What a day shorter than the standard is worth.
   *
   *   fullDay — a day marked present pays a whole day, whatever the hours.
   *             The convention for monthly-salaried staff, where a short day is
   *             a discipline question rather than a pay one.
   *   proRata — pays the fraction of the standard day actually worked. A man who
   *             leaves after three hours of an eight-hour day is paid three
   *             eighths of it.
   *
   * Never applies to a public holiday, whatever this says: a holiday has no
   * standard day, so there is no fraction to take, and the day is owed whether
   * he works it or not. Nor does it ever pay more than a full day — hours beyond
   * the standard are overtime, and they are paid as overtime.
   */
  shortDayPay: z.enum(['fullDay', 'proRata']).default('proRata'),
  roundingDecimals: z.number().int().min(0).max(4),
  /** Named allowances the company actually pays. Admin adds and removes them. */
  components: z.array(
    z.object({
      code: z.string().min(1),
      name: z.string().min(1),
      /** The same name in Arabic. Optional; falls back to `name`. */
      nameAr: z.string().optional(),
      kind: z.enum(['earning', 'deduction']),
      includeInOvertimeBase: z.boolean(),
      includeInGratuityBase: z.boolean(),
      /** Paid in full regardless of days worked, rather than pro-rated. */
      fixedMonthly: z.boolean(),
    }),
  ),
});

/* --- Geofence -------------------------------------------------------------- */
export const geofenceSchema = z.object({
  defaultRadiusM: positive.max(5000),
  /** Minutes inside the fence before a clock-in is committed. */
  entryDwellMinutes: nonNegative.max(120),
  /**
   * Minutes outside before a clock-out is committed. Should exceed the entry
   * dwell: GPS near steel structures throws a stationary phone sideways, and
   * without the wider window one man generates forty punches an afternoon.
   */
  exitDwellMinutes: nonNegative.max(120),
  minAccuracyM: positive.max(1000),
  maxSpeedKmh: positive.max(500),
  /*
   * Optional, because it is a platform limit rather than a company decision.
   *
   * iOS monitors at most 20 regions per app, Android is similar in practice.
   * Leaving it unset means "use as many as the platform allows" — a technical
   * ceiling, not a number anybody in HR has an opinion about, and nothing about
   * it changes what a man is paid.
   */
  maxMonitoredRegions: z.number().int().min(1).max(20).nullish(),
});

/* --- Absence --------------------------------------------------------------- */
export const absenceSchema = z.object({
  markAbsentAfterHour: hourOfDay,
  alertAfterDays: z.number().int().min(1),
  escalateDaily: z.boolean(),
  /** Keep alerting once someone is overdue back from approved leave. */
  alertOnOverdueReturn: z.boolean(),
});

/* --- Leave ----------------------------------------------------------------- */
export const leaveTypeSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  /** The same name in Arabic. Optional; falls back to `name`. */
  nameAr: z.string().optional(),
  paid: z.boolean(),
  /** Days earned per completed month of service. */
  accrualPerMonth: nonNegative,
  /**
   * Months of service before anything accrues at all.
   *
   * UAE practice is that annual leave becomes due once a man has been on the
   * books a year; some companies start it sooner. Set it to 0 for leave that
   * accrues from the first month, 12 for leave that starts after a year.
   *
   * Held as a number of months rather than as a rule about the law, because
   * the law is not this system's to interpret and the admin is the one who
   * knows what was signed.
   */
  minimumServiceMonths: z.number().int().min(0).max(60).default(0),
  maxCarryOverDays: nonNegative,
  requiresDocument: z.boolean(),
  requiresApproval: z.boolean(),
  countsAsService: z.boolean(),
  /**
   * Graduated pay bands, for leave that is not paid at a flat rate — e.g. a
   * first band at full pay, a second at half, a third unpaid. Leave the array
   * empty for a type paid at one rate throughout.
   */
  tiers: z.array(
    z.object({
      fromDay: z.number().int().min(1),
      toDay: z.number().int().min(1),
      payPercent: percent,
    }),
  ),
});

export const leaveSchema = z.object({
  types: z.array(leaveTypeSchema).min(1),
});

/* --- Air ticket ------------------------------------------------------------ */
/*
 * Nullish, deliberately, and it is the one block with no default anywhere.
 *
 * A ticket entitlement this system invented would be handed to somebody at an
 * airport as though the company had agreed it. Unset means the settlement
 * screen offers no opinion and the figure is typed, which is what happens today.
 */
export const ticketSchema = z.object({
  entitlementAmount: nonNegative,
  everyMonths: z.number().int().min(1),
}).nullish();

/* --- Gratuity -------------------------------------------------------------- */
export const gratuitySchema = z.object({
  enabled: z.boolean(),
  baseFor: z.enum(['basic', 'gross']),
  /** Days of pay accrued per year of service, banded by years completed. */
  tiers: z.array(
    z.object({
      fromYears: nonNegative,
      toYears: z.number().nullable(),
      daysPerYear: nonNegative,
    }),
  ),
  /*
   * Ceiling expressed in months of salary. Unset or null means no cap.
   *
   * Optional because absence errs toward the employee: no ceiling pays the full
   * accrual. A missing rate that quietly reduced somebody's gratuity would be a
   * different matter entirely and would stay mandatory.
   */
  capMonths: z.number().nullish(),
  minimumServiceYears: nonNegative,
});

/* --- Payroll --------------------------------------------------------------- */
export const payrollSchema = z.object({
  periodStartDay: z.number().int().min(1).max(28),
  requiresSecondApprover: z.boolean(),
  advanceApprovalLimit: nonNegative,
  autoDeductAdvances: z.boolean(),
  /** Cap on how much of one payslip an advance repayment may consume. */
  maxAdvanceDeductionPercent: percent,
});

/* --- The whole document ---------------------------------------------------- */
export const policySchema = z.object({
  workday: workdaySchema,
  overtime: overtimeSchema,
  salary: salarySchema,
  geofence: geofenceSchema,
  absence: absenceSchema,
  leave: leaveSchema,
  ticket: ticketSchema,
  gratuity: gratuitySchema,
  payroll: payrollSchema,
});

/* -----------------------------------------------------------------------------
 * Types come from policyTypes.ts, not from z.infer.
 *
 * The calculators run on Deno inside an Edge Function, where importing Zod is
 * needless friction, so they depend on plain interfaces instead. The assertions
 * below make the compiler prove that this schema and those interfaces are still
 * the same shape — in both directions, so neither a new schema field nor a new
 * interface field can slip through unmatched.
 * -------------------------------------------------------------------------- */
import type { Policy } from './policyTypes';

export type {
  Policy, WorkdayPolicy, OvertimePolicy, SalaryPolicy, SalaryComponent,
  GeofencePolicy, AbsencePolicy, LeavePolicy, LeaveType, LeaveTier,
  GratuityPolicy, GratuityTier, PayrollPolicy,
} from './policyTypes';

type SchemaOutput = z.infer<typeof policySchema>;
type Extends<A extends B, B> = A;

// If either line errors, the Zod schema and the interfaces have diverged.
// Fix whichever one is actually wrong — never silence this.
export type _SchemaMatchesInterface = Extends<SchemaOutput, Policy>;
export type _InterfaceMatchesSchema = Extends<Policy, SchemaOutput>;

export interface PolicyVersion {
  id: string;
  companyId: string;
  version: number;
  name: string;
  status: 'draft' | 'active' | 'superseded';
  effectiveFrom: string;
  effectiveTo: string | null;
  policy: Partial<Policy>;
  lockedAt: string | null;
  note: string | null;
}

/* =============================================================================
 * Validation
 * ========================================================================== */

export interface PolicyGap {
  path: string;
  section: string;
  message: string;
}

/**
 * Lists what the admin still has to fill in. Drives the setup wizard's progress
 * indicator and the "why can't I run payroll?" explanation, so the answer is
 * always a specific list of fields rather than a generic refusal.
 */
export function findPolicyGaps(policy: unknown): PolicyGap[] {
  const result = policySchema.safeParse(policy);
  if (result.success) return [];

  return result.error.issues.map((issue) => {
    const path = issue.path.join('.');
    return {
      path,
      section: String(issue.path[0] ?? 'general'),
      message:
        issue.code === 'invalid_type' && issue.received === 'undefined'
          ? 'Not set yet'
          : issue.message,
    };
  });
}

export function isPolicyComplete(policy: unknown): policy is Policy {
  return policySchema.safeParse(policy).success;
}

/**
 * Throws rather than returning a partial. Every calculator takes a `Policy`,
 * never a `Partial<Policy>`, so an incomplete configuration cannot reach the
 * arithmetic at all — it fails loudly at the boundary instead.
 */
export function requirePolicy(policy: unknown): Policy {
  const result = policySchema.safeParse(policy);
  if (!result.success) {
    const gaps = findPolicyGaps(policy);
    throw new PolicyIncompleteError(gaps);
  }
  return result.data;
}

export class PolicyIncompleteError extends Error {
  constructor(public readonly gaps: PolicyGap[]) {
    super(
      /* One sentence with the list dropped into it, rather than a sentence
         concatenated onto a list. Built the other way it is a different string
         every time and matches no entry, so this — the message somebody sees
         when payroll refuses to run — went out in English. */
      docSentence(
        'Company rules are incomplete — {1} setting(s) still unset: {2}',
        currentLanguage() === 'ar' ? 'ar' : 'en',
      )
        .replace('{1}', String(gaps.length))
        .replace('{2}', gaps.slice(0, 5).map((g) => g.path).join(', ')
          + (gaps.length > 5
            ? docSentence(', and {1} more', currentLanguage() === 'ar' ? 'ar' : 'en')
              .replace('{1}', String(gaps.length - 5))
            : '')),
    );
    this.name = 'PolicyIncompleteError';
  }
}

/**
 * The sections of the setup wizard, in the order an admin should meet them.
 * `blocksPayroll` marks the ones that must be finished before a single figure
 * can be calculated; the rest can be completed later.
 */
export const POLICY_SECTIONS = [
  { key: 'workday',  title: 'Working day',      blurb: 'Hours, rest days and when a day starts',        blocksPayroll: true },
  { key: 'overtime', title: 'Overtime',         blurb: 'Multipliers, night window and caps',            blocksPayroll: true },
  { key: 'salary',   title: 'Salary & rates',   blurb: 'How monthly pay becomes a daily and hourly rate', blocksPayroll: true },
  { key: 'payroll',  title: 'Payroll run',      blurb: 'Period dates, approvals and advance recovery',  blocksPayroll: true },
  { key: 'geofence', title: 'Site detection',   blurb: 'Radius, dwell times and GPS quality limits',    blocksPayroll: false },
  { key: 'absence',  title: 'Absence alerts',   blurb: 'When to flag someone and how often to repeat',  blocksPayroll: false },
  { key: 'leave',    title: 'Leave types',      blurb: 'Entitlements, accrual and pay bands',           blocksPayroll: true },
  { key: 'gratuity', title: 'End of service',   blurb: 'Accrual tiers and the calculation base',        blocksPayroll: false },
] as const;

export type PolicySectionKey = (typeof POLICY_SECTIONS)[number]['key'];
